import Card, { CardContent } from '@cobliteam/ui/components/Card';
import type { Column, Table as ITable } from '@tanstack/react-table';
import type React from 'react';
import DataTableColumns from '../components/DataTableColumns';
import DataTableFilters from '../components/DataTableFilters';
import DataTableRefresh from '../components/DataTableRefresh';
import ExportComponent, { type ExportDef } from '../components/Export';
import type { IFiltersDef, TFilterKeys } from '../types';
import DataTableSearchInput from './DataTableSearchInput';

export interface DataTableToolbarProps<TData> {
  filtersOnSecondRow: boolean;
  search: string | false;
  searchValue: string;
  defaultPlaceholder: string;
  isFetching?: boolean;
  isLoading?: boolean;
  datePicker?: React.ReactNode;
  filtersDropdown?: React.ReactNode;
  showColumnFilter?: boolean;
  columns: Column<TData>[];
  visibleColumns: Column<TData>[];
  exportDef?: ExportDef<TData>;
  data?: TData[];
  table: ITable<TData>;
  filtersDef?: IFiltersDef[];
  filtersOptions?: { key: TFilterKeys; options: any }[];
  filtersLoading?: boolean;
  favoriteFilters: TFilterKeys[];
  live?: React.ReactNode;
  handleChangeSearch: (value: string) => void;
  onRefresh?: () => void;
  Export?: (table: ITable<TData>, data: TData[], exportDef?: ExportDef<TData>) => JSX.Element;
  onChangeFilter: (value: IFiltersDef) => void;
  onSelectedFiltersChange: (keys: TFilterKeys[]) => void;
}

type ToolbarActionsProps<TData> = Pick<DataTableToolbarProps<TData>, 'showColumnFilter' | 'columns' | 'visibleColumns' | 'exportDef' | 'Export' | 'data' | 'table'>;

function ToolbarActions<TData>({ showColumnFilter = true, columns, visibleColumns, exportDef, Export, data, table }: ToolbarActionsProps<TData>) {
  return (
    <div className='ml-auto flex shrink-0 items-center gap-2'>
      {showColumnFilter && <DataTableColumns columns={columns} visibleColumns={visibleColumns} />}
      {exportDef && Export && data ? Export(table, data, exportDef) : null}
      {!exportDef && Export && data ? Export(table, data, exportDef) : null}
      {exportDef && !Export && data ? <ExportComponent data={data} table={table} exportDef={exportDef} /> : null}
    </div>
  );
}

type ToolbarFiltersRowProps<TData> = Pick<
  DataTableToolbarProps<TData>,
  'filtersDef' | 'filtersOptions' | 'filtersLoading' | 'onChangeFilter' | 'onSelectedFiltersChange' | 'favoriteFilters'
> & { filtersOnSecondRow: boolean; wrapperClassName?: string; trailing?: React.ReactNode };

function ToolbarFiltersRow<TData>({
  filtersDef,
  filtersOptions,
  filtersLoading,
  onChangeFilter,
  onSelectedFiltersChange,
  favoriteFilters,
  filtersOnSecondRow,
  wrapperClassName,
  trailing,
}: ToolbarFiltersRowProps<TData>) {
  if (!filtersDef?.length) return null;
  return (
    <div className={wrapperClassName}>
      <DataTableFilters
        filtersDef={filtersDef}
        filtersOptions={filtersOptions}
        filtersLoading={filtersLoading}
        onChangeFilter={onChangeFilter}
        onSelectedFiltersChange={onSelectedFiltersChange}
        favoriteFilters={favoriteFilters}
        filtersOnSecondRow={filtersOnSecondRow}
        trailing={trailing}
      />
    </div>
  );
}

type ToolbarFiltersPanelProps<TData> = ToolbarFiltersRowProps<TData> & {
  hasFilters: boolean;
  panelClassName: string;
  fallbackClassName?: string;
};

function ToolbarFiltersPanel<TData>({ hasFilters, panelClassName, fallbackClassName = 'ml-auto', trailing, ...filtersRowProps }: ToolbarFiltersPanelProps<TData>) {
  if (!(hasFilters || trailing)) return null;

  return <div className={panelClassName}>{hasFilters ? <ToolbarFiltersRow {...filtersRowProps} trailing={trailing} /> : <div className={fallbackClassName}>{trailing}</div>}</div>;
}

type ToolbarInlineFiltersProps<TData> = ToolbarFiltersRowProps<TData> & {
  hasFilters: boolean;
  fallbackClassName: string;
};

function ToolbarInlineFilters<TData>({ hasFilters, fallbackClassName, trailing, ...filtersRowProps }: ToolbarInlineFiltersProps<TData>) {
  if (hasFilters) return <ToolbarFiltersRow {...filtersRowProps} trailing={trailing} />;
  if (trailing) return <div className={fallbackClassName}>{trailing}</div>;
  return null;
}

const searchInputProps = (value: string, placeholder: string, onChange: (v: string) => void) => ({
  value: value || '',
  placeholder,
  onChange,
});

function DataTableToolbar<TData>({
  filtersOnSecondRow,
  search,
  searchValue,
  defaultPlaceholder,
  handleChangeSearch,
  onRefresh,
  isFetching,
  isLoading,
  datePicker,
  filtersDropdown,
  showColumnFilter,
  columns,
  visibleColumns,
  exportDef,
  Export,
  data,
  table,
  filtersDef,
  filtersOptions,
  filtersLoading,
  onChangeFilter,
  onSelectedFiltersChange,
  favoriteFilters,
  live,
}: DataTableToolbarProps<TData>) {
  const hasFilters = !!filtersDef?.length;
  const searchProps = searchInputProps(searchValue, defaultPlaceholder, handleChangeSearch);

  return (
    <Card>
      <CardContent className='flex flex-col gap-2'>
        {/* Mobile / tablet: single row for controls, then filters */}
        <div className='flex laptop:hidden flex-col gap-2'>
          <div className='flex min-w-0 items-center gap-2'>
            <DataTableRefresh onRefresh={onRefresh} isFetching={isFetching || isLoading} />
            {search !== false && (
              <div className='min-w-0 flex-1'>
                <DataTableSearchInput {...searchProps} />
              </div>
            )}
            {datePicker && <div className='shrink-0'>{datePicker}</div>}
            <ToolbarActions showColumnFilter={showColumnFilter} columns={columns} visibleColumns={visibleColumns} exportDef={exportDef} Export={Export} data={data} table={table} />
          </div>
          <ToolbarFiltersPanel
            hasFilters={hasFilters}
            filtersDef={filtersDef}
            filtersOptions={filtersOptions}
            filtersLoading={filtersLoading}
            onChangeFilter={onChangeFilter}
            onSelectedFiltersChange={onSelectedFiltersChange}
            favoriteFilters={favoriteFilters}
            filtersOnSecondRow
            wrapperClassName='flex flex-grow items-center gap-4 overflow-hidden'
            trailing={filtersDropdown}
            panelClassName='flex items-center gap-4 overflow-hidden border-gray-6 border-t pt-2'
          />
        </div>

        {/* Laptop+: keep filters on a second row whenever the available width is constrained. */}
        <div className='laptop:flex hidden flex-col gap-2'>
          <div className='flex min-w-0 items-center gap-2'>
            {live && <div className='shrink-0'>{live}</div>}
            <DataTableRefresh onRefresh={onRefresh} isFetching={isFetching || isLoading} />
            {search !== false && (
              <div className='relative tablet:max-w-64 shrink-0'>
                <DataTableSearchInput {...searchProps} />
              </div>
            )}
            {datePicker && <div className='shrink-0'>{datePicker}</div>}
            {!filtersOnSecondRow && (
              <ToolbarInlineFilters
                hasFilters={hasFilters}
                filtersDef={filtersDef}
                filtersOptions={filtersOptions}
                filtersLoading={filtersLoading}
                onChangeFilter={onChangeFilter}
                onSelectedFiltersChange={onSelectedFiltersChange}
                favoriteFilters={favoriteFilters}
                filtersOnSecondRow={false}
                wrapperClassName='flex min-w-0 flex-grow items-center gap-4 overflow-hidden'
                trailing={filtersDropdown}
                fallbackClassName='flex min-w-0 flex-grow items-center justify-end overflow-hidden'
              />
            )}
            <ToolbarActions showColumnFilter={showColumnFilter} columns={columns} visibleColumns={visibleColumns} exportDef={exportDef} Export={Export} data={data} table={table} />
          </div>
          {filtersOnSecondRow && (
            <ToolbarFiltersPanel
              hasFilters={hasFilters}
              filtersDef={filtersDef}
              filtersOptions={filtersOptions}
              filtersLoading={filtersLoading}
              onChangeFilter={onChangeFilter}
              onSelectedFiltersChange={onSelectedFiltersChange}
              favoriteFilters={favoriteFilters}
              filtersOnSecondRow
              wrapperClassName='flex min-w-0 flex-grow items-center gap-4 overflow-hidden'
              trailing={filtersDropdown}
              panelClassName='flex min-w-0 items-center gap-4 overflow-hidden border-gray-6 border-t pt-2'
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default DataTableToolbar;
